/**
 * The knowledge resources `wayai knowledge migrate` moves, read from WayAI's setup routes and
 * reduced to one shape for an org's resources and a hub's alike (#5142 step 4, K1 and K2).
 *
 * Each file is addressed by the path agents read it at today: `@wayai/core/resource-paths`
 * derives it, the same rule `list_files` and `read_file` resolve, so `resources/<slug>/<path>`
 * becomes `data/<file type>/<path>` with `<path>` unchanged. An org resource's rows are mapped to
 * the shape its hub copies take (a copy's display path is its file name), so an org file lands
 * where a hub that links the resource shows it.
 *
 * Pure: the rows come in, the reduced resources go out.
 */

import {
  buildFolderPathMap,
  buildResourcePathIndex,
  folderChainOf,
  type ResourcePathFileRow,
  type ResourcePathFolderRow,
  type ResourceSlugSource,
} from '@wayai/core/resource-paths';
import type {
  ListOrgResourceFilesResponse,
  ListOrgResourceFoldersResponse,
  ListOrgResourcesResponse,
  ListResourceFilesResponse,
  ListResourceFoldersResponse,
  ListResourcesResponse,
} from '@wayai/core/contracts';
import { guessMimeType } from '../lib/resource-files.js';

export type Metadata = Record<string, unknown>;

export type OrgResourceRow = ListOrgResourcesResponse['data'][number];
export type OrgFileRow = ListOrgResourceFilesResponse['data'][number];
export type OrgFolderRow = ListOrgResourceFoldersResponse['data'][number];
export type HubResourceRow = ListResourcesResponse['data'][number];
export type HubFileRow = ListResourceFilesResponse['data'][number];
export type HubFolderRow = ListResourceFoldersResponse['data'][number];

/** Where one file's bytes are read from: an org file by its id, a hub file by its stored object. */
export type ContentRef = { kind: 'org'; orgFileId: string } | { kind: 'hub'; bucket: string; objectKey: string };

/** A folder, in the shape the path module walks, with what the migration reads off it. */
export interface SourceFolder extends ResourcePathFolderRow {
  folder_name: string;
  parent_folder_id: string | null;
  description: string | null;
  metadata: Metadata;
}

export interface SourceFile {
  id: string;
  /** The path within the resource, as agents address it today. */
  path: string;
  folderId: string | null;
  title: string;
  tags: string[];
  metadata: Metadata;
  contentType: string;
  /** Its size in bytes, when the row states it. */
  size: number | null;
  /** Null when the row names no stored object, so there is nothing to copy. */
  content: ContentRef | null;
}

export interface SourceResource {
  id: string;
  /**
   * What the resource is, stated so a file type can say which resource it came from: the same
   * resource finds its file type again on a later run, under the id it was given (K1, K7).
   */
  sourceKey: string;
  name: string;
  description: string;
  /** The resource's path slug: `resources/<slug>` today. */
  slug: string;
  folderSchema: unknown;
  fileSchema: unknown;
  folders: SourceFolder[];
  files: SourceFile[];
  /** Disabled files: no agent reads them, and they have no path to move to. */
  disabledFiles: number;
}

/** The rows of one resource, as the setup routes return them. */
export interface ResourceRows<F, D> {
  files: F[];
  folders: D[];
}

/** An org resource the org's move carries: its knowledge, never its skills. */
export function movesFromOrg(resource: OrgResourceRow): boolean {
  return resource.resource_type === 'knowledge';
}

/** A hub resource the hub's move carries: its own knowledge; its copies of org resources are the org's. */
export function movesFromHub(resource: HubResourceRow): boolean {
  return resource.resource_type === 'knowledge' && !resource.org_resource_id;
}

function objectOf(value: unknown): Metadata {
  return value && typeof value === 'object' && !Array.isArray(value) ? { ...(value as Metadata) } : {};
}

function stringsOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((t): t is string => typeof t === 'string' && t !== '') : [];
}

function contentTypeOf(mimeType: string | null | undefined, name: string): string {
  return (mimeType && mimeType.trim()) || guessMimeType(name) || 'application/octet-stream';
}

/** One resource as the reduction reads it, whichever side it came from. */
interface ResourceView extends ResourceSlugSource {
  sourceKey: string;
  resource_name: string;
  description: string;
  folderSchema: unknown;
  fileSchema: unknown;
  moves: boolean;
}

/** One file as the reduction reads it: the path module's fields, and what the file carries. */
interface FileView extends ResourcePathFileRow {
  title: string;
  tags: string[];
  metadata: Metadata;
  contentType: string;
  size: number | null;
  content: ContentRef | null;
}

interface FolderView extends SourceFolder {
  resource_id: string;
}

/**
 * The resources that move, each with its files at their paths. One path index over every file
 * and folder of the side: a slug is ranked across the side's whole resource list, skills
 * included, as the tools rank it, and folder ids are unique across resources.
 */
function reduce(resources: ResourceView[], files: FileView[], folders: FolderView[]): SourceResource[] {
  const index = buildResourcePathIndex(resources, files, folders);
  return resources
    .filter((resource) => resource.moves)
    .map((resource) => ({
      id: resource.resource_id,
      sourceKey: resource.sourceKey,
      name: resource.resource_name,
      description: resource.description,
      slug: index.slugByResourceId.get(resource.resource_id) ?? '',
      folderSchema: resource.folderSchema,
      fileSchema: resource.fileSchema,
      folders: folders
        .filter((folder) => folder.resource_id === resource.resource_id)
        .map(({ resource_id: _resource, ...folder }) => folder),
      files: (index.entriesByResourceId.get(resource.resource_id) ?? []).map(({ relative_path, file }) => ({
        id: file.resource_file_id,
        path: relative_path,
        folderId: file.folder_id ?? null,
        title: file.title,
        tags: file.tags,
        metadata: file.metadata,
        contentType: file.contentType,
        size: file.size,
        content: file.content,
      })),
      disabledFiles: files.filter((file) => file.resource_id === resource.resource_id && file.enabled === false).length,
    }));
}

/**
 * An org's knowledge resources. `resources` is the org's whole list (skills too: they take part in
 * the slug ranking); `rows` holds each moving resource's files and folders.
 */
export function orgSources(
  resources: OrgResourceRow[],
  rows: Map<string, ResourceRows<OrgFileRow, OrgFolderRow>>,
): SourceResource[] {
  const moving = resources.filter(movesFromOrg).map((r) => rows.get(r.org_resource_id) ?? { files: [], folders: [] });
  return reduce(
    resources.map((r) => ({
      resource_id: r.org_resource_id,
      resource_name: r.resource_name,
      skill_name: r.skill_name,
      resource_type: r.resource_type,
      created_at: r.created_at,
      sourceKey: `organization resource ${r.org_resource_id}`,
      description: r.resource_description ?? '',
      folderSchema: r.folder_metadata_schema,
      fileSchema: r.file_metadata_schema,
      moves: movesFromOrg(r),
    })),
    // A hub's copy of an org file carries the file name as its display path, so mapped that way
    // here the path module derives the path that hub shows.
    moving.flatMap(({ files }) =>
      files.map((f) => ({
        resource_id: f.org_resource_id,
        resource_file_id: f.org_resource_file_id,
        file_id: f.file_id,
        resource_file_title: f.resource_file_title,
        folder_id: f.folder_id,
        file_path: f.file_name,
        order_index: f.order_index,
        enabled: f.enabled,
        created_at: f.created_at,
        file: { file_name: f.file_name },
        title: f.resource_file_title ?? '',
        tags: stringsOf(f.resource_file_tags),
        metadata: objectOf(f.metadata),
        contentType: contentTypeOf(f.mime_type, f.file_name),
        size: typeof f.file_size === 'number' ? f.file_size : null,
        content: { kind: 'org' as const, orgFileId: f.org_resource_file_id },
      })),
    ),
    moving.flatMap(({ folders }) =>
      folders.map((d) => ({
        resource_id: d.org_resource_id,
        resource_folder_id: d.org_resource_folder_id,
        folder_name: d.folder_name,
        parent_folder_id: d.parent_folder_id,
        description: d.folder_description,
        metadata: objectOf(d.metadata),
      })),
    ),
  );
}

/**
 * A production hub's own knowledge resources. `resources` is the hub's whole list, for the slug
 * ranking; `rows` holds each moving resource's files and folders.
 */
export function hubSources(
  hubId: string,
  resources: HubResourceRow[],
  rows: Map<string, ResourceRows<HubFileRow, HubFolderRow>>,
): SourceResource[] {
  const moving = resources.filter(movesFromHub).map((r) => rows.get(r.resource_id) ?? { files: [], folders: [] });
  return reduce(
    resources.map((r) => ({
      resource_id: r.resource_id,
      resource_name: r.resource_name,
      skill_name: r.skill_name,
      resource_type: r.resource_type,
      created_at: r.created_at,
      sourceKey: `hub ${hubId} resource ${r.resource_id}`,
      description: r.resource_description ?? '',
      folderSchema: r.folder_metadata_schema,
      fileSchema: r.file_metadata_schema,
      moves: movesFromHub(r),
    })),
    moving.flatMap(({ files }) =>
      files.map((f) => ({
        resource_id: f.resource_id,
        resource_file_id: f.resource_file_id,
        file_id: f.file_id,
        resource_file_title: f.resource_file_title,
        folder_id: f.folder_id,
        file_path: f.file_path,
        order_index: f.order_index,
        enabled: f.enabled,
        created_at: f.created_at,
        file: f.file,
        title: f.resource_file_title ?? '',
        tags: stringsOf(f.resource_file_tags),
        metadata: objectOf(f.metadata),
        contentType: contentTypeOf(f.file?.mime_type, f.file?.file_name ?? f.resource_file_title ?? ''),
        size: typeof f.file?.file_size === 'number' ? f.file.file_size : null,
        content: f.file?.file_path ? { kind: 'hub' as const, bucket: f.file.bucket, objectKey: f.file.file_path } : null,
      })),
    ),
    moving.flatMap(({ folders }) =>
      folders.map((d) => ({
        resource_id: d.resource_id,
        resource_folder_id: d.resource_folder_id,
        folder_name: d.folder_name,
        parent_folder_id: d.parent_folder_id,
        description: d.folder_description,
        metadata: objectOf(d.metadata),
      })),
    ),
  );
}

/**
 * A folder and its ancestors, nearest first; empty for a dangling, cyclic or over-deep chain, the
 * chains no folder path is derived from (the path module's own walk).
 */
export function folderChain(folderId: string | null, byId: ReadonlyMap<string, SourceFolder>): SourceFolder[] {
  return folderId ? (folderChainOf(folderId, byId) ?? []) : [];
}

/** Every folder's own path within its resource (`a/b`), by folder id, as the path module derives it. */
export function folderPaths(folders: SourceFolder[]): Map<string, string> {
  return buildFolderPathMap(folders);
}
